import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "Work", href: "#work" },
  { label: "Services", href: "#services" },
  { label: "Inside the Lab", href: "#inside-the-lab" },
  { label: "From the Lab", href: "#from-the-lab" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-[#0b0618]/80 backdrop-blur-xl border-b border-[#2D1B4E]"
            : "bg-transparent"
        }`}
      >
        <nav className="mx-auto max-w-[1500px] px-6 md:px-12 h-20 flex items-center justify-between">
          <a
            href="#home"
            data-testid="nav-logo"
            className="group inline-flex items-center"
          >
            <span className="font-display font-extrabold text-xl md:text-2xl tracking-tight text-white">
              CMD<span className="text-[#C77DFF]">+</span>DesignLab
              <sup className="align-super text-[0.5em] text-[#C77DFF] ml-0.5">
                &reg;
              </sup>
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="link-underline text-sm uppercase tracking-[0.18em] text-[#B5A6CC] hover:text-white transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
          </div>

          <a
            href="#contact"
            data-testid="nav-start-project"
            className="hidden lg:inline-flex items-center gap-2 rounded-full bg-[#8A2BE2] hover:bg-[#9D4EDD] text-white text-sm font-medium pl-6 pr-2 py-2 transition-colors duration-300"
          >
            Start a Project
            <span className="grid place-items-center h-7 w-7 rounded-full bg-white/15">
              <ArrowUpRight size={15} />
            </span>
          </a>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            className="lg:hidden text-white p-2"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#090514]/98 backdrop-blur-2xl flex flex-col p-6"
          >
            <div className="flex justify-end">
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-white p-2"
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            <div className="flex flex-col gap-6 mt-10">
              {[...LINKS, { label: "Start a Project", href: "#contact" }].map(
                (l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1 + i * 0.07 }}
                    className="font-display text-4xl font-bold text-white"
                  >
                    {l.label}
                  </motion.a>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
